import { useIntl } from "react-intl";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { InfoButton } from "../../components/Tooltips";
import {
  getModelLabel,
  METRIC_EXPLANATIONS,
  METRICS,
  MODEL_COLORS,
  pct,
} from "./constants";
import { MLMetrics } from "./services";

const tableModelColor = MODEL_COLORS["prob_vente"];

const MetricHeaderCell = ({
  metricKey,
}: {
  metricKey: keyof typeof METRIC_EXPLANATIONS;
}) => {
  const info = METRIC_EXPLANATIONS[metricKey]!;
  return (
    <TableCell align="right" sx={{ fontWeight: 700 }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        {info.label}
        <InfoButton
          title={
            <Box sx={{ maxWidth: 280 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                {info.simple}
              </Typography>
              <Typography variant="caption">{info.detail}</Typography>
            </Box>
          }
          translateTitle={false}
        />
      </Box>
    </TableCell>
  );
};

const MetricValue = ({
  value,
  best,
}: {
  value: number | null;
  best: boolean;
}) => (
  <Box
    sx={{
      fontWeight: best ? 700 : 400,
      color: best ? tableModelColor : "text.primary",
    }}
  >
    {value === null ? "—" : pct(value)}
  </Box>
);

const metricValue = (m: MLMetrics, key: string) =>
  m[key as keyof MLMetrics] as number | null;

export interface MetricsTableRow {
  m: MLMetrics;
  name: string;
}

const MetricsTable = ({
  rows,
  onSelectModel,
}: {
  rows: MetricsTableRow[];
  onSelectModel: (modelName: string) => void;
}) => {
  const intl = useIntl();

  return (
    <Paper elevation={1}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {intl.formatMessage({ id: "ML_ANALYSIS_COL_MODEL" })}
              </TableCell>
              {METRICS.map((key) => (
                <MetricHeaderCell key={key} metricKey={key} />
              ))}
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {intl.formatMessage({ id: "ML_ANALYSIS_COL_TRAIN" })}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {intl.formatMessage({ id: "ML_ANALYSIS_COL_TEST" })}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {intl.formatMessage({ id: "ML_ANALYSIS_COL_DATE" })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ m, name }) => (
              <TableRow
                key={name}
                onClick={m.is_active ? undefined : () => onSelectModel(name)}
                sx={{
                  opacity: m.is_active ? 1 : 0.4,
                  cursor: m.is_active ? "default" : "pointer",
                  transition: "opacity 0.2s",
                  "&:hover": m.is_active ? {} : { opacity: 0.7 },
                  "& td:first-of-type": {
                    borderLeft: `3px solid ${
                      m.is_active ? tableModelColor : "transparent"
                    }`,
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {m.is_active ? (
                      <RadioButtonCheckedIcon
                        sx={{
                          color: tableModelColor,
                          fontSize: 18,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{
                          color: "text.disabled",
                          fontSize: 18,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={getModelLabel(name)}
                      sx={{
                        backgroundColor: m.is_active
                          ? "primary.main"
                          : "transparent",
                        color: m.is_active
                          ? "primary.contrastText"
                          : "text.secondary",
                        border: "1px solid",
                        borderColor: m.is_active ? "primary.main" : "divider",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </TableCell>
                {METRICS.map((key) => {
                  const value = metricValue(m, key);
                  const others = rows
                    .map((r) => metricValue(r.m, key))
                    .filter((v): v is number => v !== null);
                  return (
                    <TableCell key={key} align="right">
                      <MetricValue
                        value={value}
                        best={
                          rows.length > 1 &&
                          value !== null &&
                          value >= Math.max(...others)
                        }
                      />
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  {intl.formatNumber(m.n_train)}
                </TableCell>
                <TableCell align="right">
                  {intl.formatNumber(m.n_test)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                >
                  {intl.formatDate(m.trained_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MetricsTable;
